/**
 * @voltxampere/web — Bookmark IDB persistence bridge (Sprint 32-4)
 *
 * Glues the in-memory bookmarks signal (Sprint 32-3) to the
 * `bookmarks` IndexedDB store (DB v4). The pattern mirrors
 * `commands/recent.ts`: hydrate once at boot, fire-and-forget
 * writes on every mutation, swallow IDB errors with a console warn.
 *
 * Failure mode:
 *  - IDB unavailable (private mode) → in-memory signal still works
 *    for the current session; bookmarks vanish on reload.
 *  - IDB read fails at boot → signal stays empty; mutations still
 *    attempt to persist so the user's next add at least tries IDB.
 *  - IDB write fails → console.warn, no UI surface (the add already
 *    succeeded in-memory; we can't roll back without surprising the
 *    user).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect } from "@preact/signals-core";

import {
  clearBookmarks as idbClearBookmarks,
  deleteBookmark as idbDeleteBookmark,
  listAllBookmarks,
  putBookmark,
  type StoredBookmark,
} from "@voltxampere/persistence";

import { bookmarksSignal, hydrateBookmarks, type Bookmark } from "./bookmarks.js";

let hydrated = false;
let writeWatcherDisposer: (() => void) | null = null;
/** Snapshot used to diff signal changes into per-row IDB writes. */
let lastSnapshot: readonly Bookmark[] = [];

/**
 * Hydrate the in-memory bookmarks signal from IDB. Called once
 * during boot. Returns the number of rows loaded.
 */
export async function initBookmarks(): Promise<number> {
  if (hydrated) return bookmarksSignal.value.length;
  try {
    const rows = await listAllBookmarks();
    const bookmarks: Bookmark[] = rows.map((r) => ({
      id: r.id,
      circuitId: r.circuitId,
      t: r.t,
      note: r.note,
      createdAt: r.createdAt,
      // Sprint 53 — round-trip the optional speed snapshot.
      ...(typeof r.speed === "number" ? { speed: r.speed } : {}),
    }));
    hydrateBookmarks(bookmarks);
    lastSnapshot = bookmarksSignal.value;
    hydrated = true;
    startWriteWatcher();
    return bookmarks.length;
  } catch (err) {
    console.warn("[VXA bookmarks] IDB hydration failed; bookmarks start empty.", err);
    hydrated = true;
    startWriteWatcher();
    return 0;
  }
}

/**
 * Subscribe to bookmarksSignal and mirror every change to IDB. Diffs
 * against the prior snapshot so a single add → one putBookmark, a
 * single delete → one idbDeleteBookmark, an update → one putBookmark.
 */
function startWriteWatcher(): void {
  if (writeWatcherDisposer !== null) return;
  writeWatcherDisposer = effect(() => {
    const next = bookmarksSignal.value;
    const prev = lastSnapshot;
    lastSnapshot = next;
    diffAndPersist(prev, next);
  });
}

function diffAndPersist(prev: readonly Bookmark[], next: readonly Bookmark[]): void {
  const prevById = new Map(prev.map((b) => [b.id, b]));
  const nextById = new Map(next.map((b) => [b.id, b]));

  for (const [id, bookmark] of nextById) {
    const before = prevById.get(id);
    if (before === undefined || !sameBookmark(before, bookmark)) {
      void putBookmark(toStored(bookmark)).catch((err: unknown) => {
        console.warn(`[VXA bookmarks] Failed to persist '${id}' to IDB:`, err);
      });
    }
  }

  for (const id of prevById.keys()) {
    if (!nextById.has(id)) {
      void idbDeleteBookmark(id).catch((err: unknown) => {
        console.warn(`[VXA bookmarks] Failed to delete '${id}' from IDB:`, err);
      });
    }
  }
}

function sameBookmark(a: Bookmark, b: Bookmark): boolean {
  return (
    a.id === b.id &&
    a.circuitId === b.circuitId &&
    a.t === b.t &&
    a.note === b.note &&
    a.createdAt === b.createdAt &&
    a.speed === b.speed
  );
}

function toStored(b: Bookmark): StoredBookmark {
  return {
    id: b.id,
    circuitId: b.circuitId,
    t: b.t,
    note: b.note,
    createdAt: b.createdAt,
    // Sprint 53 — optional speed multiplier snapshot.
    ...(typeof b.speed === "number" ? { speed: b.speed } : {}),
  };
}

/**
 * Wipe every bookmark — both signal and IDB. Wired into the AYARLAR
 * "Veri" tab "Tümünü temizle" button (Sprint 21 C5 lineage).
 */
export async function clearAllBookmarks(): Promise<void> {
  hydrateBookmarks([]);
  lastSnapshot = [];
  try {
    await idbClearBookmarks();
  } catch (err) {
    console.warn("[VXA bookmarks] IDB clear failed:", err);
  }
}

/** Test-only — reset hydration flag and watcher. */
export function __resetBookmarksPersistenceForTests(): void {
  hydrated = false;
  if (writeWatcherDisposer !== null) {
    writeWatcherDisposer();
    writeWatcherDisposer = null;
  }
  lastSnapshot = [];
}
